import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import LanguageFlag, { LANG_META } from '../components/LanguageFlag';
import { useI18n } from '../contexts/I18nContext';
import {
  Youtube, Plus, Loader2, Clock, BookOpen, Sparkles, Trash2, Play,
  Wand2, PenLine, Flame, Star, Eye,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LANG_OPTIONS = Object.entries(LANG_META).map(([code, m]) => ({ code, name: m.name }));

const LEVELS = ['A1', 'A2', 'B1', 'B2'];

// Pretty duration — switches format/colour over 30 min
function formatDuration(s) {
  if (!s) return '—';
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    return `${h}:${String(m % 60).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
  }
  return `${m}:${String(r).padStart(2, '0')}`;
}

// Estimated reading time (subtitles at ~80% of video length)
function estReadTime(sec) {
  if (!sec) return null;
  const est = Math.round((sec * 0.8) / 60);
  return est >= 1 ? `${est} min read` : '<1 min';
}

// YYYYMMDD → "Apr 16, 2026"
function formatUploadDate(yyyymmdd) {
  if (!yyyymmdd || yyyymmdd.length !== 8) return null;
  const y = yyyymmdd.slice(0, 4), m = +yyyymmdd.slice(4, 6), d = +yyyymmdd.slice(6, 8);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[m - 1]} ${d}, ${y}`;
}

// Split the title on the first em-dash / colon so long headlines render nicely
function splitTitle(title) {
  if (!title) return { headline: '', subhead: '' };
  // Common separators used in documentary / news titles
  const separators = ['——', ' — ', ' – ', ' - ', '：', ': '];
  for (const sep of separators) {
    const idx = title.indexOf(sep);
    if (idx > 4 && idx < title.length - 4) {
      return { headline: title.slice(0, idx).trim(), subhead: title.slice(idx + sep.length).trim() };
    }
  }
  return { headline: title, subhead: '' };
}

// -------- VIDEO CARD --------
function VideoCard({ v, onOpen, onDelete }) {
  const { headline, subhead } = splitTitle(v.title || '');
  const dur = v.duration_seconds || 0;
  const isLong = dur >= 30 * 60;
  const actionBadge = v.has_subs
    ? null
    : dur <= 45 * 60
      ? { label: '🤖 Transcribable', cls: 'bg-violet-600/90 text-white' }
      : { label: '📝 Paste subs', cls: 'bg-amber-600/90 text-white' };
  const read = estReadTime(dur);
  const uploaded = formatUploadDate(v.upload_date);

  return (
    <Card
      className="overflow-hidden group cursor-pointer hover:shadow-xl hover:ring-1 hover:ring-primary/30 transition-all"
      onClick={onOpen}
      data-testid={`video-card-${v.external_id}`}
    >
      <div className="relative aspect-video overflow-hidden bg-muted">
        <img
          src={v.thumbnail_url}
          alt={v.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
          onError={(e) => {
            e.target.src = `https://img.youtube.com/vi/${v.external_id}/hqdefault.jpg`;
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/35 transition-colors">
          <Play className="h-12 w-12 text-white fill-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        {/* Duration pill — red over 30min */}
        <Badge className={`absolute bottom-2 right-2 ${isLong ? 'bg-red-600/90 text-white' : 'bg-black/80 text-white'} gap-1`}>
          <Clock className="h-3 w-3" />
          {formatDuration(dur)}
        </Badge>
        {/* Language flag top-left */}
        <div className="absolute top-2 left-2 bg-black/70 rounded-full px-2 py-1 flex items-center gap-1.5 backdrop-blur-sm">
          <LanguageFlag code={v.language_code} size="sm" />
          <span className="text-[10px] font-medium text-white uppercase tracking-wide">
            {v.language_code}
          </span>
        </div>
        {/* Action badge top-right */}
        {actionBadge && (
          <Badge className={`absolute top-2 right-2 ${actionBadge.cls}`}>{actionBadge.label}</Badge>
        )}
        {isLong && !actionBadge && (
          <Badge className="absolute top-2 right-2 bg-red-600/90 text-white">Long-form</Badge>
        )}
      </div>
      <CardContent className="pt-4 pb-3 space-y-2">
        {/* Meta ribbon */}
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground truncate">
          {v.category && <span className="inline-flex items-center gap-1"><span className="text-amber-500">●</span>{v.category}</span>}
          {v.channel && <span className="truncate max-w-[140px]" title={v.channel}>{v.channel}</span>}
          {uploaded && <span className="whitespace-nowrap">· {uploaded}</span>}
        </div>
        {/* Title */}
        <div>
          <h3 className="font-semibold text-sm leading-snug line-clamp-1 group-hover:text-primary transition-colors">
            {headline}
          </h3>
          {subhead && (
            <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
              {subhead}
            </p>
          )}
        </div>
        {/* Stats */}
        <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1">
          <span className="inline-flex items-center gap-1" title="New words">
            <Sparkles className="h-3 w-3 text-amber-500" /> {v.new_words || 0}
          </span>
          <span className="inline-flex items-center gap-1" title="Unique words">
            <BookOpen className="h-3 w-3" /> {v.unique_words || 0}
          </span>
          {read && (
            <span className="inline-flex items-center gap-1" title="Estimated read time">
              <PenLine className="h-3 w-3" /> {read}
            </span>
          )}
          {v.view_count > 0 && (
            <span className="inline-flex items-center gap-1" title="YouTube views">
              <Eye className="h-3 w-3" /> {v.view_count > 1000 ? `${Math.round(v.view_count / 1000)}K` : v.view_count}
            </span>
          )}
          {onDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(v); }}
              className="ml-auto text-muted-foreground hover:text-red-500 transition-colors"
              data-testid={`delete-video-${v.external_id}`}
              title="Remove"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// -------- FEATURED CARD (smaller, level-first) --------
function FeaturedCard({ v, onImport, busy }) {
  const dur = v.duration_seconds || 0;
  const { headline, subhead } = splitTitle(v.title || '');
  return (
    <Card
      className="overflow-hidden group cursor-pointer hover:shadow-lg transition-all"
      onClick={() => onImport(v)}
      data-testid={`featured-card-${v.external_id}`}
    >
      <div className="relative aspect-video bg-muted overflow-hidden">
        <img
          src={v.thumbnail_url}
          alt={v.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
          onError={(e) => { e.target.src = `https://img.youtube.com/vi/${v.external_id}/hqdefault.jpg`; }}
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
        <Badge className="absolute top-2 left-2 bg-indigo-600 text-white text-[10px] uppercase tracking-wider">
          {v.level}
        </Badge>
        {v.topic && (
          <Badge className="absolute top-2 right-2 bg-black/70 text-white text-[10px] capitalize">{v.topic}</Badge>
        )}
        <Badge className={`absolute bottom-2 right-2 ${dur >= 30 * 60 ? 'bg-red-600/90' : 'bg-black/80'} text-white`}>
          <Clock className="h-3 w-3 mr-1" />
          {formatDuration(dur)}
        </Badge>
        <div className="absolute bottom-2 left-2 bg-black/70 rounded-full px-2 py-1 flex items-center gap-1.5 backdrop-blur-sm">
          <LanguageFlag code={v.language_code} size="sm" />
          <span className="text-[10px] font-medium text-white uppercase">{v.language_code}</span>
        </div>
        {busy && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-white" />
          </div>
        )}
      </div>
      <CardContent className="pt-3 pb-3 space-y-1">
        <p className="text-[10px] text-muted-foreground truncate">{v.channel}</p>
        <h3 className="font-semibold text-sm leading-snug line-clamp-1">{headline}</h3>
        {subhead && <p className="text-xs text-muted-foreground line-clamp-1">{subhead}</p>}
        {!v.has_subs_preflight && (
          <Badge className="text-[10px] bg-amber-500/20 text-amber-500 border border-amber-500/40">
            No native subs
          </Badge>
        )}
      </CardContent>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════════
export default function VideoLibrary() {
  const navigate = useNavigate();
  const { t } = useI18n();

  // --- My Videos ---
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState('');
  const [lang, setLang] = useState('en');
  const [importing, setImporting] = useState(false);

  // --- Featured ---
  const [featured, setFeatured] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(false);
  const [featLang, setFeatLang] = useState('es');
  const [featLevel, setFeatLevel] = useState('all');
  const [featureLangStats, setFeatureLangStats] = useState({});  // { es: { A1: 3, ... } }
  const [importingId, setImportingId] = useState(null);

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/video-library`, { withCredentials: true });
      setVideos(res.data?.videos || []);
    } catch (err) {
      console.error('Failed to list videos:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFeatured = useCallback(async () => {
    setFeaturedLoading(true);
    // Use Promise.allSettled so a failure in one endpoint can't blank the whole tab.
    const params = { language_code: featLang };
    if (featLevel !== 'all') params.level = featLevel;
    const [listRes, langsRes] = await Promise.allSettled([
      axios.get(`${BACKEND_URL}/api/video-library/featured/list`, { params, withCredentials: true }),
      axios.get(`${BACKEND_URL}/api/video-library/featured/languages`, { withCredentials: true }),
    ]);
    if (listRes.status === 'fulfilled') {
      setFeatured(listRes.value.data?.videos || []);
    } else {
      console.error('Failed to list featured videos:', listRes.reason);
      // keep prior list in place if the call failed transiently
    }
    if (langsRes.status === 'fulfilled') {
      setFeatureLangStats(langsRes.value.data?.languages || {});
    } else {
      console.error('Failed to fetch featured language stats:', langsRes.reason);
    }
    setFeaturedLoading(false);
  }, [featLang, featLevel]);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);
  useEffect(() => { fetchFeatured(); }, [fetchFeatured]);

  const handleImport = async (e) => {
    e?.preventDefault();
    if (!url.trim()) return;
    setImporting(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/video-library/import`,
        { url: url.trim(), language_code: lang },
        { withCredentials: true }
      );
      const data = res.data || {};
      // Backend now returns 200 with {ok:false, message} on logical failures.
      if (data.ok === false) {
        toast.error(data.message || 'Import failed');
        return;
      }
      toast.success(`Imported "${data.title}"`);
      if (data.message) toast.info(data.message);
      setUrl('');
      await fetchVideos();
      if (data.needs_manual_subs && !data.message) {
        toast.info(t('videoLibrary.noCaptionsToast'));
      }
      navigate(`/videos/${data.video_id}?lang=${lang}`);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.detail || 'Import failed. Check the URL and your connection.';
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  const handleImportFeatured = async (v) => {
    setImportingId(v.external_id);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/video-library/featured/${v.external_id}/import?language_code=${v.language_code}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Added "${res.data.title}" to your library`);
      await fetchVideos();
      navigate(`/videos/${res.data.video_id}?lang=${v.language_code}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally {
      setImportingId(null);
    }
  };

  const handleDelete = async (video) => {
    if (!window.confirm(`Remove "${video.title}" from your video library?`)) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/video-library/${video.video_id}?lang=${video.language_code}`,
        { withCredentials: true }
      );
      toast.success(t('videoLibrary.removed'));
      setVideos((vs) =>
        vs.filter((x) => x.video_id !== video.video_id || x.language_code !== video.language_code)
      );
    } catch {
      toast.error(t('videoLibrary.removeError'));
    }
  };

  const featLangsWithContent = Object.keys(featureLangStats);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="video-library-page">
      <div className="flex items-center gap-3 mb-6">
        <Youtube className="h-8 w-8 text-red-500" />
        <div>
          <h1 className="text-3xl font-bold">{t('videoLibrary.title')}</h1>
          <p className="text-muted-foreground text-sm">
            Learn with YouTube. Subtitles become a clickable, interactive reader.
          </p>
        </div>
      </div>

      <Tabs defaultValue="mine" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="mine" data-testid="tab-my-videos">
            My Videos {videos.length > 0 && <span className="ml-1 text-xs">({videos.length})</span>}
          </TabsTrigger>
          <TabsTrigger value="featured" data-testid="tab-featured">
            <Star className="h-3 w-3 mr-1" /> Featured
          </TabsTrigger>
        </TabsList>

        {/* ════════ MY VIDEOS ════════ */}
        <TabsContent value="mine">
          <Card className="mb-8">
            <CardContent className="pt-6">
              <form onSubmit={handleImport} className="flex flex-col sm:flex-row gap-3">
                <Input
                  placeholder={t('videoLibrary.addVideoPlaceholder')}
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  disabled={importing}
                  className="flex-1"
                  data-testid="video-url-input"
                />
                <Select value={lang} onValueChange={setLang} disabled={importing}>
                  <SelectTrigger className="w-full sm:w-56" data-testid="video-lang-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANG_OPTIONS.map((l) => (
                      <SelectItem key={l.code} value={l.code}>
                        <span className="flex items-center gap-2">
                          <LanguageFlag code={l.code} size="sm" />
                          {l.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="submit"
                  disabled={importing || !url.trim()}
                  className="gap-2"
                  data-testid="video-import-btn"
                >
                  {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  Import
                </Button>
              </form>
              <p className="text-xs text-muted-foreground mt-3">
                We'll fetch captions in your chosen language. Without captions, you can auto-transcribe with Whisper or paste subtitles.
              </p>
            </CardContent>
          </Card>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading library…
            </div>
          ) : videos.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Youtube className="h-16 w-16 mx-auto mb-3 opacity-30" />
              <p>{t('videoLibrary.noVideos')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="video-grid">
              {videos.map((v) => (
                <VideoCard
                  key={`${v.video_id}-${v.language_code}`}
                  v={v}
                  onOpen={() => navigate(`/videos/${v.video_id}?lang=${v.language_code}`)}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* ════════ FEATURED ════════ */}
        <TabsContent value="featured">
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <Select value={featLang} onValueChange={setFeatLang}>
              <SelectTrigger className="w-48" data-testid="featured-lang-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANG_OPTIONS.filter(l => featLangsWithContent.includes(l.code) || l.code === featLang).map(l => (
                  <SelectItem key={l.code} value={l.code}>
                    <span className="flex items-center gap-2">
                      <LanguageFlag code={l.code} size="sm" />
                      {l.name}
                      {featureLangStats[l.code] && (
                        <span className="text-xs text-muted-foreground">
                          ({Object.values(featureLangStats[l.code]).reduce((a, b) => a + b, 0)})
                        </span>
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant={featLevel === 'all' ? 'default' : 'outline'}
                onClick={() => setFeatLevel('all')}
                className="text-xs"
              >
                All
              </Button>
              {LEVELS.map((lv) => (
                <Button
                  key={lv}
                  size="sm"
                  variant={featLevel === lv ? 'default' : 'outline'}
                  onClick={() => setFeatLevel(lv)}
                  className="text-xs"
                  data-testid={`featured-level-${lv}`}
                >
                  {lv}
                </Button>
              ))}
            </div>
          </div>

          {featuredLoading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading featured videos…
            </div>
          ) : featured.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Flame className="h-16 w-16 mx-auto mb-3 opacity-30" />
              <p className="font-medium">
                No featured videos for {(featLang || '').toUpperCase()}{featLevel !== 'all' ? ` / ${featLevel}` : ''} yet.
              </p>
              {Object.keys(featureLangStats || {}).length > 0 && (
                <div className="mt-4 max-w-md mx-auto text-xs">
                  <p className="opacity-80 mb-2">{t('videoLibrary.trySimilar')}</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {Object.entries(featureLangStats)
                      .map(([lc, levels]) => ({ lc, levels, total: Object.values(levels).reduce((s, n) => s + n, 0) }))
                      .sort((a, b) => b.total - a.total)
                      .slice(0, 8)
                      .map(({ lc, total }) => (
                        <button
                          key={lc}
                          onClick={() => { setFeatLang(lc); setFeatLevel('all'); }}
                          className="px-2.5 py-1 rounded-full border hover:bg-muted transition-colors"
                          data-testid={`featured-empty-suggest-${lc}`}
                        >
                          {lc.toUpperCase()} ({total})
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="featured-grid">
              {featured.map((v) => (
                <FeaturedCard
                  key={v.external_id}
                  v={v}
                  onImport={handleImportFeatured}
                  busy={importingId === v.external_id}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
